import { cn } from '@/lib/utils'
import { AUTOMATION_PLATFORMS, type AutomationPlatform } from '@backend/lib/db/schema/automation'
import { PLATFORM_LABELS, PLATFORM_STYLES, platformIcons, togglePlatform } from './constants'

const SIZES = {
  xs: { circle: 'h-5 w-5', icon: 11 },
  sm: { circle: 'h-7 w-7', icon: 14 },
  md: { circle: 'h-9 w-9', icon: 18 },
} as const

/** A single platform as a brand-coloured circle with a white glyph. */
export function PlatformIcon({
  platform,
  size = 'md',
  active = true,
  className,
}: {
  platform: AutomationPlatform
  size?: 'xs' | 'sm' | 'md'
  active?: boolean
  className?: string
}) {
  const Icon = platformIcons[platform]
  const s = SIZES[size]
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full text-white',
        active ? PLATFORM_STYLES[platform] : 'bg-gray-400 opacity-50',
        s.circle,
        className,
      )}>
      <Icon width={s.icon} height={s.icon} color="#FFFFFF" />
    </span>
  )
}

/** Read-only cluster of the platforms an automation posts to. */
export function PlatformIcons({
  platforms,
  size = 'sm',
}: {
  platforms: AutomationPlatform[]
  size?: 'xs' | 'sm' | 'md'
}) {
  return (
    <div className="flex shrink-0 items-center gap-1">
      {AUTOMATION_PLATFORMS.filter((p) => platforms.includes(p)).map((p) => (
        <PlatformIcon key={p} platform={p} size={size} />
      ))}
    </div>
  )
}

/** Overlapping stack of an automation's platform icons — compact card summary. */
export function PlatformStack({ platforms }: { platforms: AutomationPlatform[] }) {
  return (
    <div className="flex shrink-0 -space-x-2">
      {AUTOMATION_PLATFORMS.filter((p) => platforms.includes(p)).map((p) => (
        <PlatformIcon key={p} platform={p} size="sm" className="ring-card rounded-full ring-2" />
      ))}
    </div>
  )
}

/** Selectable brand-coloured platform circles; the last selected one locks (min 1). */
export function PlatformSelect({
  value,
  onChange,
  size = 'md',
}: {
  value: AutomationPlatform[]
  onChange: (next: AutomationPlatform[]) => void
  size?: 'sm' | 'md'
}) {
  return (
    <div className="flex items-center gap-2">
      {AUTOMATION_PLATFORMS.map((p) => {
        const selected = value.includes(p)
        const isLast = selected && value.length === 1
        return (
          <button
            key={p}
            type="button"
            aria-pressed={selected}
            aria-label={PLATFORM_LABELS[p]}
            title={isLast ? 'At least one platform required' : PLATFORM_LABELS[p]}
            onClick={() => {
              if (!isLast) onChange(togglePlatform(value, p))
            }}
            className={cn(
              'rounded-full transition-opacity',
              !selected && 'hover:opacity-90',
              isLast && 'cursor-not-allowed',
            )}>
            <PlatformIcon platform={p} active={selected} size={size} />
          </button>
        )
      })}
    </div>
  )
}
